import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaSave, FaEdit } from 'react-icons/fa';
import { useColorMode, Spinner, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ModifierArticle = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [description, setDescription] = useState('');
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/blog/${id}`);
                const { title, content, description } = response.data;
                setTitle(title);
                setContent(content);
                setDescription(description);
            } catch (error) {
                console.error("Erreur lors du chargement:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
    }, [id, API_URL]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content);
        formData.append('description', description);
        if (image) formData.append('image', image);

        const token = localStorage.getItem('token');
        try {
            await axios.put(`${API_URL}/api/blog/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            navigate('/blog');
        } catch (error) {
            console.error(error);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return (
        <Box h="100vh" display="flex" flexDir="column" alignItems="center" justifyContent="center" bg={isDark ? "#0a0f16" : "#FAFCFF"}>
            <Spinner size="xl" thickness="4px" color="blue.600" />
            <p className="mt-4 text-[10px] font-black uppercase tracking-widest opacity-40">Récupération de l'article</p>
        </Box>
    );

    return (
        <Box className={isDark ? 'bg-[#0a0f16] min-h-screen' : 'bg-[#FAFCFF] min-h-screen'}>
            <div className="max-w-4xl mx-auto px-6 py-32">
                <button onClick={() => navigate(-1)} className={`flex items-center gap-3 mb-12 text-xs font-black uppercase tracking-widest opacity-60 hover:opacity-100 transition-all ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    <FaArrowLeft /> Annuler & Retour
                </button>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`rounded-[2.5rem] shadow-2xl overflow-hidden border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                    <div className="p-6 bg-blue-600 text-white flex items-center justify-center gap-3">
                        <FaEdit />
                        <span className="font-black uppercase tracking-widest text-[10px]">Édition de l'article</span>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">
                        <div className="space-y-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Titre de l'article</label>
                                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required 
                                    className={`w-full p-5 rounded-2xl border outline-none transition-all font-bold ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600 focus:bg-white'}`} 
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Résumé court</label>
                                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} 
                                    className={`w-full p-5 rounded-2xl border outline-none transition-all font-light ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600 focus:bg-white'}`} 
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Contenu éditorial</label>
                                <div className={`rounded-2xl overflow-hidden border transition-all ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                    <ReactQuill theme="snow" value={content} onChange={setContent} className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-[#1B365D]'}`} />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Mise à jour de l'image</label>
                                <div className={`relative p-8 rounded-2xl border-2 border-dashed text-center ${isDark ? 'border-gray-700 hover:border-blue-600' : 'border-gray-200 hover:border-blue-600'}`}>
                                    <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                    <p className="text-xs font-bold opacity-60 italic">{image ? image.name : "Cliquez ou glissez une nouvelle image ici"}</p>
                                </div>
                            </div>
                        </div>

                        <button type="submit" disabled={updating} 
                            className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-600/20 transition-all flex items-center justify-center gap-4 group"
                        >
                            {updating ? <Spinner size="sm" /> : <><FaSave className="group-hover:scale-125 transition-transform" /> Enregistrer les modifications[cite: 3]</>}
                        </button>
                    </form>
                </motion.div>
            </div>
        </Box>
    );
};

export default ModifierArticle;